import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import client, { API } from "../lib/api";
import { Shield, Skull, ArrowLeft, Calendar, ChevronRight, Trophy } from "lucide-react";

export default function Survivors() {
    const [summary, setSummary] = useState(null);
    const [list, setList] = useState([]);
    const [tab, setTab] = useState("survivors");

    useEffect(() => {
        client.get("/elimination-summary").then((r) => setSummary(r.data));
        client.get("/elimination-summary/list").then((r) => setList(r.data));
    }, []);

    if (!summary)
        return (
            <div className="px-5 pt-8">
                <Link to="/app" className="inline-flex items-center text-zinc-500">
                    <ArrowLeft size={18} />
                    <span className="ml-2 label-cap text-xs">Home</span>
                </Link>
                <div className="mt-10 surface-1 p-8 text-center text-zinc-400">
                    No elimination yet. Survive this week to be on the wall.
                </div>
            </div>
        );

    const items = tab === "survivors" ? summary.survivors : summary.eliminated;

    return (
        <div className="px-5 pt-8 pb-8">
            <Link to="/app" className="inline-flex items-center text-zinc-500 hover:text-white">
                <ArrowLeft size={18} />
                <span className="ml-2 label-cap text-xs">Home</span>
            </Link>

            <div className="mt-4">
                <span className="label-cap text-xs text-neon-orange flex items-center gap-1">
                    <Calendar size={10} /> Week {summary.week_key}
                </span>
                <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Survivors Wall</h1>
                <p className="text-zinc-400 text-sm mt-1">{summary.challenge_title}</p>
                {summary.challenge_tagline && (
                    <p className="text-neon-orange text-xs mt-1 label-cap">{summary.challenge_tagline}</p>
                )}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                    onClick={() => setTab("survivors")}
                    data-testid="survivors-tab-survivors"
                    className={`p-4 transition-all ${
                        tab === "survivors"
                            ? "border-2 border-neon-green"
                            : "surface-1"
                    }`}
                    style={tab === "survivors" ? { background: "#062006", boxShadow: "0 0 16px rgba(57,255,20,0.2)" } : {}}
                >
                    <Shield size={18} className="text-neon-green mx-auto mb-1" />
                    <div className="font-display font-black text-3xl text-neon-green">{summary.survivor_count}</div>
                    <div className="label-cap text-[10px] text-zinc-500">Survivors</div>
                </button>
                <button
                    onClick={() => setTab("eliminated")}
                    data-testid="survivors-tab-eliminated"
                    className={`p-4 transition-all ${
                        tab === "eliminated"
                            ? "border-2 border-neon-orange"
                            : "surface-1"
                    }`}
                    style={tab === "eliminated" ? { background: "#1a0606", boxShadow: "0 0 16px rgba(255,59,48,0.25)" } : {}}
                >
                    <Skull size={18} className="text-neon-orange mx-auto mb-1" />
                    <div className="font-display font-black text-3xl text-neon-orange">{summary.eliminated_count}</div>
                    <div className="label-cap text-[10px] text-zinc-500">Eliminated</div>
                </button>
            </div>

            <div className="mt-6 space-y-2">
                {items.length === 0 ? (
                    <div className="surface-1 p-6 text-center text-zinc-400">No one in this list.</div>
                ) : (
                    items.map((u, i) => (
                        <motion.div
                            key={u.id}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            data-testid={`survivor-${u.id}`}
                            className="surface-1 p-3 flex items-center gap-3"
                        >
                            <div
                                className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center"
                                style={{
                                    background: tab === "survivors" ? "#39FF1420" : "#FF3B3020",
                                    border: `1px solid ${tab === "survivors" ? "#39FF14" : "#FF3B30"}`,
                                }}
                            >
                                {u.has_photo ? (
                                    <img src={`${API}/users/${u.id}/photo`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <span className="font-display font-black text-sm">
                                        {u.name?.[0]?.toUpperCase()}
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold uppercase text-sm leading-none truncate">
                                    {u.name}
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">
                                    Lv {u.current_level} · {u.streak} streak
                                </div>
                            </div>
                            {tab === "survivors" ? (
                                <Shield size={16} className="text-neon-green" />
                            ) : (
                                <Skull size={16} className="text-neon-orange" />
                            )}
                        </motion.div>
                    ))
                )}
            </div>

            {list.length > 1 && (
                <div className="mt-8">
                    <h3 className="font-display font-black uppercase text-base tracking-tight">Past Weeks</h3>
                    <div className="mt-2 space-y-2">
                        {list.map((s) => (
                            <div
                                key={s.week_key}
                                className="surface-1 p-3 flex items-center gap-3"
                                data-testid={`past-week-${s.week_key}`}
                            >
                                <Trophy size={14} className="text-zinc-500" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold uppercase text-sm leading-none truncate">
                                        {s.challenge_title}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-1">
                                        {s.week_key} · {s.survivor_count} survived · {s.eliminated_count} out
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
